import { useCallback, useEffect, useRef } from "react";
import { BUILD_VERSION } from "../../buildInfo";

const CHECK_INTERVAL_MS = 90 * 1000;
const VERIFY_DELAY_MS = 1500;
const USER_IDLE_MS = 15 * 1000;
const RETRY_COOLDOWN_MS = 5 * 60 * 1000;
const ATTEMPT_KEY = "hm_auto_version_refresh";

async function getLatestVersion() {
  const response = await fetch(`/version.json?ts=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });
  if (!response.ok) return null;
  return response.json();
}

function hasBlockingInteraction() {
  const active = document.activeElement;
  const editing = active && (
    ["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName)
    || active.isContentEditable
  );
  return Boolean(editing || document.querySelector('[aria-modal="true"], [role="dialog"]'));
}

function recentlyAttempted(version) {
  try {
    const attempt = JSON.parse(window.sessionStorage.getItem(ATTEMPT_KEY) || "null");
    return attempt?.version === version && Date.now() - Number(attempt.at || 0) < RETRY_COOLDOWN_MS;
  } catch {
    return false;
  }
}

async function prepareServiceWorkerUpdate() {
  if (!("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.update().catch(() => null)));
}

export default function VersionAutoUpdater() {
  const lastInteractionAt = useRef(0);
  const updateInProgress = useRef(false);

  const checkVersion = useCallback(async () => {
    if (process.env.NODE_ENV !== "production" || updateInProgress.current) return;
    try {
      const latest = await getLatestVersion();
      if (!latest?.version || latest.version === BUILD_VERSION) {
        window.sessionStorage.removeItem(ATTEMPT_KEY);
        return;
      }
      if (recentlyAttempted(latest.version)) return;
      if (document.visibilityState !== "visible" || hasBlockingInteraction()) return;
      if (Date.now() - lastInteractionAt.current < USER_IDLE_MS) return;

      // Confirm the deployment marker is stable before reloading. This avoids
      // catching a hosting edge while version.json and the new bundle differ.
      await new Promise((resolve) => window.setTimeout(resolve, VERIFY_DELAY_MS));
      const confirmed = await getLatestVersion();
      if (confirmed?.version !== latest.version) return;

      updateInProgress.current = true;
      window.sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({
        version: latest.version,
        at: Date.now(),
      }));
      await prepareServiceWorkerUpdate();
      window.location.reload();
    } catch {
      // Updating must never block normal HouseMind use. The next scheduled,
      // focus, or visibility check will retry quietly.
      updateInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    const recordInteraction = () => { lastInteractionAt.current = Date.now(); };
    const onVisible = () => {
      if (document.visibilityState === "visible") checkVersion();
    };
    const initial = window.setTimeout(checkVersion, 1200);
    const interval = window.setInterval(checkVersion, CHECK_INTERVAL_MS);
    window.addEventListener("pointerdown", recordInteraction, { passive: true });
    window.addEventListener("keydown", recordInteraction);
    window.addEventListener("focus", checkVersion);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(interval);
      window.removeEventListener("pointerdown", recordInteraction);
      window.removeEventListener("keydown", recordInteraction);
      window.removeEventListener("focus", checkVersion);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkVersion]);

  return null;
}
